import React, { Dispatch, SetStateAction, JSXElementConstructor } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import type { GlyphProps, CustomGlyphProps } from "@atlaskit/icon/types";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { JiraTemplateChoice } from "../../../hooks";
import {
    getIconForTransparentTemplateV2,
    getTeamTypeForTransparentTemplateV2,
    UserSegmentationTeamTypes
} from "../../../models/user-segmentation";
import {
    TemplateOption,
    TemplateOptionMetadata
} from "../team-type-template-map";

import Card from "./card";

interface BodyProps {
    setSelectedTemplate: Dispatch<SetStateAction<JiraTemplateChoice>>;
    cards: TemplateOption[];
    setSelectedTemplateId: Dispatch<SetStateAction<string | undefined>>;
    selectedCardTitle: string | undefined;
    setSelectedCardTitle: Dispatch<SetStateAction<string | undefined>>;
    teamType: UserSegmentationTeamTypes;
    setSelectedTemplateMetadata: Dispatch<
        SetStateAction<TemplateOptionMetadata | undefined>
    >;
}

const templateChoiceWrapper = css({
    padding: token("space.300"),
    maxWidth: "1216px",
    borderRadius: "18px",
    border: `1px solid ${token("color.background.accent.gray.subtlest")}`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "space-evenly",
    backgroundColor: "#FAFBFC",
    [media.below.sm]: {
        marginBlockEnd: "10px",
        minHeight: " 430px",
        padding: "12px"
    }
});

const buttonWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    flexWrap: "wrap",
    margin: "0 auto",
    padding: "0px",
    border: "none",
    gap: token("space.150"),
    [media.above.md]: {
        gap: token("space.400"),
        flexWrap: "nowrap",
        flexDirection: "row"
    }
});

const headerContainerStyle = css({
    display: "flex",
    flexDirection: "row",
    justifyContent: "center"
});

const headerStyle = css({
    fontWeight: 700,
    fontSize: "24px",
    lineHeight: "30px",
    marginBlockStart: "10px"
});

const iconStyle = css({
    marginBlockStart: "-3px"
});

const Body: React.FC<BodyProps> = ({
    setSelectedTemplate,
    cards,
    setSelectedTemplateId,
    selectedCardTitle,
    setSelectedCardTitle,
    teamType,
    setSelectedTemplateMetadata
}) => {
    const intl = useIntl();
    const identityTeamType = getTeamTypeForTransparentTemplateV2(
        teamType,
        intl.formatMessage
    );
    const Icon:
        | JSXElementConstructor<GlyphProps>
        | JSXElementConstructor<CustomGlyphProps>
        | undefined
        | null = getIconForTransparentTemplateV2(teamType);
    return (
        <div css={templateChoiceWrapper}>
            <div css={headerContainerStyle}>
                <div css={iconStyle}>{Icon && <Icon />}</div>
                <h4 css={headerStyle}>{identityTeamType}</h4>
            </div>
            {/* Tech debt that should be fixed. See ldr/0012-jsx-a11y-strict.md for details. */}
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role */}
            <fieldset role="radiogroup" css={buttonWrapperStyle}>
                {cards.map((card, index) => (
                    <Card
                        key={index}
                        {...card.option}
                        setSelectedTemplate={setSelectedTemplate}
                        setSelectedTemplateId={setSelectedTemplateId}
                        selectedCardTitle={selectedCardTitle}
                        setSelectedCardTitle={setSelectedCardTitle}
                        teamType={teamType}
                        templateMetadata={card.metadata}
                        setSelectedTemplateMetadata={
                            setSelectedTemplateMetadata
                        }
                    />
                ))}
            </fieldset>
        </div>
    );
};

export default Body;
